/* import __COLOCATED_TEMPLATE__ from './reasons-interactive-bar.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { inject as service } from '@ember/service';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import type AppService from 'embercom/services/app-service';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { use } from 'ember-resources/util/function-resource';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import {
  and,
  not,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import {
  type OptimizeSuggestionItem,
  OptimizeSuggestionType,
} from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/constants';

export interface ReasonData {
  share: number;
  sentToTeammatePercentage: number;
}

interface Signature {
  Args: {
    isEmptyState: boolean;
    suggestions: OptimizeSuggestionItem[];
  };
}

type Segment = {
  suggestionType: OptimizeSuggestionType;
  label: string;
  colorClass: string;
  share: number;
};

export default class ReasonsInteractiveBar extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;
  @service declare appService: AppService;
  @service declare reportingChartService: ReportingChartService;

  @use teammateHandledConversationsResource = staticDataResource(this, {
    serializedChart: () => this.teammateInvolvedChartDefinition,
  });

  @use totalConversationsResource = staticDataResource(this, {
    serializedChart: () => this.countChartDefinition,
  });

  get countChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [{ metric_id: 'v1.new_conversations', filters: this.finInvolved }],
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get teammateInvolvedChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: and(this.finInvolved, this.teammateWasEverInvolved, not(this.finResolved)),
        },
      ],
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get finInvolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.participated',
        attribute: 'conversation.fin.participated',
        values: [true],
      },
    };
  }

  get teammateWasEverInvolved() {
    return {
      type: 'exists',
      data: {
        property: 'admin_participant_ids',
        attribute: 'conversation.admin_participant_ids',
      },
    };
  }

  get finResolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.resolution_state',
        attribute: 'conversation.fin.resolution_state',
        values: ['confirmed_resolved', 'assumed_resolved'],
      },
    };
  }

  get teammateHandledConversationsPercentage() {
    let teammateHandledConversations = this.teammateHandledConversationsResource
      .kpiData[0] as number;
    let totalConversations = this.totalConversationsResource.kpiData[0] as number;

    if (totalConversations === 0) {
      return 0;
    }
    return Math.round((teammateHandledConversations / totalConversations) * 100);
  }

  get isLoading() {
    return (
      this.teammateHandledConversationsResource.isLoading ||
      this.totalConversationsResource.isLoading
    );
  }

  get contentSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter(
      (s) => s.suggestionType === OptimizeSuggestionType.ContentGap,
    );
  }

  get dataSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter((s) => s.suggestionType === OptimizeSuggestionType.DataGap);
  }

  get actionSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter(
      (s) => s.suggestionType === OptimizeSuggestionType.ActionGap,
    );
  }

  @action
  isFilteredByType(suggestionType: OptimizeSuggestionType) {
    let filters = this.optimizeSuggestionsService.filterState?.suggestionTypes;

    if (!filters || filters.length === 0) {
      return true;
    }

    return filters.includes(suggestionType);
  }

  get segments(): Segment[] {
    let total = this.args.suggestions.length;
    return [
      {
        suggestionType: OptimizeSuggestionType.ContentGap,
        label: 'CONTENT GAPS',
        colorClass: 'bg-vis-mantis-10',
        share: total > 0 ? Math.round((this.contentSuggestions.length / total) * 100) : 0,
      },
      {
        suggestionType: OptimizeSuggestionType.DataGap,
        label: 'DATA GAPS',
        colorClass: 'bg-vis-mantis-40',
        share: total > 0 ? Math.round((this.dataSuggestions.length / total) * 100) : 0,
      },
      {
        suggestionType: OptimizeSuggestionType.ActionGap,
        label: 'ACTION GAPS',
        colorClass: 'bg-vis-mantis-60',
        share: total > 0 ? Math.round((this.actionSuggestions.length / total) * 100) : 0,
      },
    ];
  }

  get gridTemplateColumns() {
    let spans = this.segments.map((segment) => (segment.share > 0 ? segment.share : 1));
    return spans.map((span) => `${span}fr`).join(' ');
  }

  @action
  filterBy(suggestionType: OptimizeSuggestionType) {
    let currentFilter = this.optimizeSuggestionsService.filterState?.suggestionTypes ?? [];
    let isAlreadyFiltered = currentFilter.length === 1 && currentFilter[0] === suggestionType;

    this.optimizeSuggestionsService.setFilterState({
      ...this.optimizeSuggestionsService.filterState,
      suggestionTypes: isAlreadyFiltered ? [] : [suggestionType],
    });
  }

  @action
  handleSelect(suggestionType: OptimizeSuggestionType) {
    this.filterBy(suggestionType);
  }

  @action
  getReasonData(suggestionType: OptimizeSuggestionType): ReasonData {
    let suggestionsByKey: Record<OptimizeSuggestionType, OptimizeSuggestionItem[]> = {
      'content-gap': this.contentSuggestions,
      'data-gap': this.dataSuggestions,
      'action-gap': this.actionSuggestions,
    };

    let suggestions = suggestionsByKey[suggestionType];
    let total = this.args.suggestions.length;
    let share = total > 0 ? (suggestions.length / total) * 100 : 0;

    return {
      share,
      sentToTeammatePercentage: this.teammateHandledConversationsPercentage,
    };
  }

  @action
  getReasonTitle(suggestionType: OptimizeSuggestionType): string {
    let titles: Record<OptimizeSuggestionType, string> = {
      'content-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.titles.content-gaps',
      ),
      'data-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.titles.data-gaps',
      ),
      'action-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.titles.action-gaps',
      ),
    };
    return titles[suggestionType];
  }

  @action
  getReasonDescription(suggestionType: OptimizeSuggestionType): string {
    let descriptions: Record<OptimizeSuggestionType, string> = {
      'content-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.content-gaps',
      ),
      'data-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.data-gaps',
      ),
      'action-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.action-gaps',
      ),
    };
    return descriptions[suggestionType];
  }

  @action
  getReasonIcon(suggestionType: OptimizeSuggestionType): InterfaceIconName {
    let icons: Record<OptimizeSuggestionType, InterfaceIconName> = {
      'content-gap': 'knowledge',
      'data-gap': 'recipient-data',
      'action-gap': 'webhook',
    };
    return icons[suggestionType];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::ReasonsInteractiveBar': typeof ReasonsInteractiveBar;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/reasons-interactive-bar': typeof ReasonsInteractiveBar;
  }
}
